import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Avatar,
  AvatarGroup,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Stack,
  Paper,
  Divider,
  Button,
  Collapse,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Forum,
  Search,
  Refresh,
  Gavel,
  SupportAgent,
  Storefront,
  Person,
  AdminPanelSettings,
  Done,
  DoneAll,
  QuestionAnswer,
  Message as MessageIcon,
  Groups,
  ShoppingCart,
  BrokenImage,
  InfoOutlined,
  ArrowForwardIos,
  Circle,
  ExpandMore,
  Visibility,
} from '@mui/icons-material';
import apiClient from '../utils/apiClient';
import { useChat } from '../contexts/ChatContext';
import { EmptyState } from '../components/common';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OversightUser {
  _id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phone?: string;
  storeName?: string;
}

interface OversightParticipant {
  userId: OversightUser | null;
  role: 'buyer' | 'seller' | 'admin' | 'party_a' | 'party_b';
  joinedAt?: string;
  isActive: boolean;
  unreadCount: number;
  lastSeenAt?: string;
}

interface OversightChat {
  _id: string;
  type: 'private' | 'group' | 'support';
  status: 'active' | 'closed' | 'dispute';
  title?: string;
  description?: string;
  participants: OversightParticipant[];
  orderId?: { _id: string; orderNumber?: string; status?: string };
  orderItemId?: { _id: string; productTitle?: string; status?: string };
  productId?: { _id: string; title?: string; slug?: string };
  relatedEntity?: { type: string; id: string };
  disputeId?: string;
  deliveryFlow?: string;
  lastMessage?: { content?: string; senderId?: string; sentAt?: string; messageType?: string };
  totalMessages: number;
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
  closeReason?: string;
}

interface OversightMessage {
  _id: string;
  senderId: string | null;
  senderName: string;
  senderRole: string;
  content?: string;
  messageType: string;
  sentAt: string;
  isSystemMessage?: boolean;
  isDeleted?: boolean;
  isEdited?: boolean;
  editedAt?: string;
  systemData?: { action?: string; metadata?: any };
  replyTo?: { messageId?: string; content?: string; senderName?: string };
  mentions?: { userId: string; name: string }[];
  readBy?: { userId: string; readAt: string }[];
  deliveredTo?: { userId: string; deliveredAt: string }[];
  file?: {
    originalName?: string;
    mimetype?: string;
    size?: number;
    url?: string;
    base64Data?: string;
  };
}

interface MonitorStats {
  total: number;
  active: number;
  dispute: number;
  closed: number;
  byType: Record<string, number>;
  messagesLast24h: number;
  chatsActiveLast24h: number;
}

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const PAGE_SIZE = 30;

const userDisplayName = (u?: OversightUser | null): string => {
  if (!u) return 'کاربر حذف‌شده';
  if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`;
  return u.name || u.email || u.phone || 'کاربر';
};

const ROLE_FA: Record<string, string> = {
  buyer: 'خریدار',
  seller: 'فروشنده',
  admin: 'ادمین',
  party_a: 'طرف اول',
  party_b: 'طرف دوم',
  user: 'کاربر',
};

const ROLE_COLOR: Record<string, string> = {
  buyer: '#0ea5e9',
  seller: '#10b981',
  admin: '#8b5cf6',
  party_a: '#0ea5e9',
  party_b: '#f59e0b',
  user: '#64748b',
};

const STATUS_META: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'default' }> = {
  active: { label: 'فعال', color: 'success' },
  dispute: { label: 'اختلاف', color: 'warning' },
  closed: { label: 'بسته‌شده', color: 'default' },
};

interface CategoryMeta {
  key: string;
  label: string;
  color: string;
  icon: React.ReactNode;
}

const categoryOfChat = (chat: OversightChat): CategoryMeta => {
  const rel = chat.relatedEntity?.type;
  if (rel === 'RequestOffer') {
    return { key: 'request', label: 'درخواست خرید', color: '#06b6d4', icon: <ShoppingCart sx={{ fontSize: 14 }} /> };
  }
  if (rel === 'Dispute' || chat.disputeId || chat.status === 'dispute') {
    return { key: 'dispute', label: 'اختلاف', color: '#ef4444', icon: <Gavel sx={{ fontSize: 14 }} /> };
  }
  if (chat.type === 'support') {
    return { key: 'support', label: 'پشتیبانی', color: '#0ea5e9', icon: <SupportAgent sx={{ fontSize: 14 }} /> };
  }
  if (chat.type === 'group') {
    return { key: 'group', label: 'گروهی', color: '#64748b', icon: <Groups sx={{ fontSize: 14 }} /> };
  }
  return { key: 'buyer_seller', label: 'خریدار ↔ فروشنده', color: '#10b981', icon: <Storefront sx={{ fontSize: 14 }} /> };
};

const formatDateTime = (value?: string | Date | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('fa-IR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatTime = (value?: string | Date | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('fa-IR', { hour: '2-digit', minute: '2-digit' }).format(date);
};

const formatRelative = (value?: string | Date | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'الان';
  if (minutes < 60) return `${minutes.toLocaleString('fa-IR')} دقیقه`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours.toLocaleString('fa-IR')} ساعت`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days.toLocaleString('fa-IR')} روز`;
  return new Intl.DateTimeFormat('fa-IR', { month: 'short', day: 'numeric' }).format(date);
};

const dayKeyOf = (value: string): string => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toDateString();
};

const dayLabelOf = (value: string): string => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  const today = new Date();
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
  if (date.toDateString() === today.toDateString()) return 'امروز';
  if (date.toDateString() === yesterday.toDateString()) return 'دیروز';
  return new Intl.DateTimeFormat('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
};

const imageSrcOf = (file?: OversightMessage['file']): string | null => {
  if (!file) return null;
  if (file.url) return file.url;
  if (file.base64Data) {
    return file.base64Data.startsWith('data:')
      ? file.base64Data
      : `data:${file.mimetype || 'image/jpeg'};base64,${file.base64Data}`;
  }
  return null;
};

const normalizeMessage = (raw: any): OversightMessage => {
  const senderId =
    raw?.senderId && typeof raw.senderId === 'object' ? raw.senderId._id?.toString() : raw?.senderId?.toString();
  return {
    ...raw,
    _id: raw._id,
    senderId: senderId || null,
    senderName: raw.senderName || 'سیستم',
    senderRole: raw.senderRole || 'user',
    sentAt: raw.sentAt || raw.createdAt || new Date().toISOString(),
  };
};

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'همه دسته‌ها' },
  { value: 'buyer_seller', label: 'خریدار ↔ فروشنده' },
  { value: 'dispute', label: 'اختلاف / داوری' },
  { value: 'request', label: 'درخواست خرید' },
  { value: 'support', label: 'پشتیبانی' },
  { value: 'group', label: 'گروهی' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'همه وضعیت‌ها' },
  { value: 'active', label: 'فعال' },
  { value: 'dispute', label: 'اختلاف' },
  { value: 'closed', label: 'بسته‌شده' },
];

/** طرفین اصلی گفتگو (بدون ادمین‌ها) برای نمایش خلاصه */
const mainParties = (chat: OversightChat): OversightParticipant[] => {
  const nonAdmin = chat.participants.filter((p) => p.role !== 'admin');
  return nonAdmin.length > 0 ? nonAdmin : chat.participants;
};

const chatDisplayTitle = (chat: OversightChat): string => {
  const parties = mainParties(chat);
  if (parties.length >= 2) {
    return parties.slice(0, 2).map((p) => userDisplayName(p.userId)).join('  ↔  ');
  }
  if (chat.title) return chat.title;
  if (parties.length === 1) return userDisplayName(parties[0].userId);
  return 'گفتگو';
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ChatOversight: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { state: chatState, joinChat, leaveChat } = useChat();

  // list
  const [chats, setChats] = useState<OversightChat[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');

  // stats
  const [stats, setStats] = useState<MonitorStats | null>(null);

  // live
  const [monitorJoined, setMonitorJoined] = useState(false);
  const [flashChats, setFlashChats] = useState<Record<string, number>>({});

  // conversation
  const [selectedChat, setSelectedChat] = useState<OversightChat | null>(null);
  const [messages, setMessages] = useState<OversightMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  const filtersRef = useRef({ search: '', category: 'all', status: 'all' });
  const lastLiveRefetchRef = useRef(0);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  filtersRef.current = { search, category, status };

  // ------------------------------------------------------------------
  // Data fetching
  // ------------------------------------------------------------------

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/admin/chats/monitor/stats');
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching chat monitor stats:', err);
    }
  }, []);

  const fetchChats = useCallback(
    async (pageArg: number, opts?: { append?: boolean; silent?: boolean }) => {
      const f = filtersRef.current;
      try {
        if (!opts?.silent) {
          if (opts?.append) setLoadingMore(true);
          else setLoading(true);
        }
        setError(null);
        const response = await apiClient.get('/admin/chats/all', {
          params: {
            page: pageArg,
            limit: PAGE_SIZE,
            category: f.category,
            status: f.status,
            search: f.search || undefined,
          },
        });
        const list: OversightChat[] = response.data.data || [];
        setTotal(response.data.pagination?.total || 0);
        setChats((prev) => {
          if (opts?.append) {
            const seen = new Set(prev.map((c) => c._id));
            return [...prev, ...list.filter((c) => !seen.has(c._id))];
          }
          if (opts?.silent) {
            // تازه‌سازی بی‌صدا: صفحهٔ اول جدید + ادامهٔ لیست قبلی (بدون پرش)
            const pageIds = new Set(list.map((c) => c._id));
            return [...list, ...prev.filter((c) => !pageIds.has(c._id))];
          }
          return list;
        });
      } catch (err: any) {
        console.error('Error fetching chats:', err);
        if (!opts?.silent) setError(err?.response?.data?.message || 'خطا در دریافت لیست چت‌ها');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // فیلترها عوض شد → از صفحهٔ اول
  useEffect(() => {
    setPage(1);
    fetchChats(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, status]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchChats(next, { append: true });
  };

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ------------------------------------------------------------------
  // Real-time: global monitor room
  // ------------------------------------------------------------------

  useEffect(() => {
    const socket = chatState.socket;
    if (!socket) return;

    const join = () => socket.emit('join_chat_monitor');
    const onJoined = () => setMonitorJoined(true);
    const onJoinError = (data: any) => {
      setMonitorJoined(false);
      console.warn('join_chat_monitor error:', data);
    };

    const onMonitorMessage = (data: { chatId: string; message: any }) => {
      if (!data?.chatId || !data?.message) return;

      setStats((prev) => (prev ? { ...prev, messagesLast24h: prev.messagesLast24h + 1 } : prev));

      setChats((prev) => {
        const idx = prev.findIndex((c) => c._id === data.chatId);
        if (idx === -1) {
          if (Date.now() - lastLiveRefetchRef.current > 2500) {
            lastLiveRefetchRef.current = Date.now();
            fetchChats(1, { silent: true });
          }
          return prev;
        }
        const updated: OversightChat = {
          ...prev[idx],
          totalMessages: (prev[idx].totalMessages || 0) + 1,
          updatedAt: data.message.sentAt || new Date().toISOString(),
          lastMessage: {
            content: data.message.isDeleted ? '' : data.message.content,
            senderId: data.message.senderId,
            sentAt: data.message.sentAt,
            messageType: data.message.messageType,
          },
        };
        const rest = prev.filter((c) => c._id !== data.chatId);
        return [updated, ...rest];
      });

      setFlashChats((prev) => ({ ...prev, [data.chatId]: Date.now() }));
      setTimeout(() => {
        setFlashChats((prev) => {
          const copy = { ...prev };
          delete copy[data.chatId];
          return copy;
        });
      }, 2600);
    };

    const onMonitorChatCreated = () => {
      setStats((prev) => (prev ? { ...prev, total: prev.total + 1, active: prev.active + 1 } : prev));
      if (Date.now() - lastLiveRefetchRef.current > 2500) {
        lastLiveRefetchRef.current = Date.now();
        fetchChats(1, { silent: true });
      }
    };

    socket.on('connect', join);
    socket.on('joined_chat_monitor', onJoined);
    socket.on('join_chat_monitor_error', onJoinError);
    socket.on('monitor_new_message', onMonitorMessage);
    socket.on('monitor_chat_created', onMonitorChatCreated);

    if (socket.connected) join();

    return () => {
      socket.emit('leave_chat_monitor');
      socket.off('connect', join);
      socket.off('joined_chat_monitor', onJoined);
      socket.off('join_chat_monitor_error', onJoinError);
      socket.off('monitor_new_message', onMonitorMessage);
      socket.off('monitor_chat_created', onMonitorChatCreated);
      setMonitorJoined(false);
    };
  }, [chatState.socket, fetchChats]);

  // ------------------------------------------------------------------
  // Real-time: open conversation
  // ------------------------------------------------------------------

  useEffect(() => {
    const socket = chatState.socket;
    if (!socket || !selectedChat?._id) return;

    const chatId = selectedChat._id;
    joinChat(chatId);

    const onNewMessage = (data: any) => {
      const incomingChatId = data?.chatId || data?.chatUpdate?._id;
      if (incomingChatId !== chatId) return;
      const raw = data.message || data;
      if (!raw?._id) return;
      setMessages((prev) => {
        if (prev.some((m) => m._id === raw._id)) return prev;
        return [...prev, normalizeMessage(raw)];
      });
      setTypingUser(null);
    };

    const onTyping = (data: { chatId: string; userId: string; isTyping: boolean }) => {
      if (data?.chatId !== chatId) return;
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      if (data.isTyping) {
        const participant = selectedChat.participants.find((p) => p.userId?._id === data.userId);
        setTypingUser(userDisplayName(participant?.userId));
        typingTimerRef.current = setTimeout(() => setTypingUser(null), 4000);
      } else {
        setTypingUser(null);
      }
    };

    const onChatClosed = (data: any) => {
      if (data?.chatId !== chatId) return;
      setSelectedChat((prev) =>
        prev && prev._id === chatId
          ? { ...prev, status: 'closed', closedAt: data.closedAt, closeReason: data.reason }
          : prev
      );
    };

    socket.on('new_message', onNewMessage);
    socket.on('user_typing', onTyping);
    socket.on('chat_closed', onChatClosed);

    return () => {
      leaveChat(chatId);
      socket.off('new_message', onNewMessage);
      socket.off('user_typing', onTyping);
      socket.off('chat_closed', onChatClosed);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      setTypingUser(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatState.socket, selectedChat?._id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // ------------------------------------------------------------------
  // Open / close conversation
  // ------------------------------------------------------------------

  const openChat = useCallback(async (chat: OversightChat) => {
    setSelectedChat(chat);
    setShowInfo(false);
    setMessages([]);
    setMessagesError(null);
    setMessagesLoading(true);
    try {
      const response = await apiClient.get(`/admin/chats/${chat._id}/messages`);
      setMessages((response.data.data || []).map(normalizeMessage));
    } catch (err: any) {
      console.error('Error fetching messages:', err);
      setMessagesError(err?.response?.data?.message || 'خطا در دریافت پیام‌های چت');
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const closeConversation = useCallback(() => {
    setSelectedChat(null);
    setMessages([]);
    setMessagesError(null);
  }, []);

  // ------------------------------------------------------------------
  // Small render pieces
  // ------------------------------------------------------------------

  const liveOk = monitorJoined && chatState.isConnected;

  const statItems = [
    { label: 'کل چت‌ها', value: stats?.total, icon: <Forum sx={{ fontSize: 18 }} />, color: '#0ea5e9' },
    { label: 'فعال', value: stats?.active, icon: <QuestionAnswer sx={{ fontSize: 18 }} />, color: '#10b981' },
    { label: 'اختلاف', value: stats?.dispute, icon: <Gavel sx={{ fontSize: 18 }} />, color: '#ef4444' },
    { label: 'پیام ۲۴س', value: stats?.messagesLast24h, icon: <MessageIcon sx={{ fontSize: 18 }} />, color: '#8b5cf6' },
    { label: 'چت فعال ۲۴س', value: stats?.chatsActiveLast24h, icon: <Visibility sx={{ fontSize: 18 }} />, color: '#f59e0b' },
  ];

  const roleAvatar = (p: OversightParticipant, size = 34) => {
    const color = ROLE_COLOR[p.role] || '#64748b';
    const name = userDisplayName(p.userId);
    return (
      <Avatar
        key={`${p.userId?._id}-${p.role}`}
        sx={{
          width: size,
          height: size,
          fontSize: size * 0.42,
          fontWeight: 700,
          bgcolor: alpha(color, isDark ? 0.35 : 0.18),
          color,
          border: `2px solid ${alpha(color, 0.6)}`,
        }}
      >
        {name.charAt(0)}
      </Avatar>
    );
  };

  const renderChatListItem = (chat: OversightChat) => {
    const cat = categoryOfChat(chat);
    const parties = mainParties(chat);
    const isSelected = selectedChat?._id === chat._id;
    const isFlashing = !!flashChats[chat._id];
    const lastPreview =
      chat.lastMessage?.messageType === 'image'
        ? '🖼️ تصویر'
        : chat.lastMessage?.content || 'بدون پیام';

    return (
      <Box
        key={chat._id}
        onClick={() => openChat(chat)}
        sx={{
          display: 'flex',
          gap: 1.25,
          px: 1.5,
          py: 1.25,
          cursor: 'pointer',
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderInlineStart: `3px solid ${isSelected ? cat.color : 'transparent'}`,
          bgcolor: isSelected
            ? alpha(cat.color, isDark ? 0.16 : 0.08)
            : isFlashing
              ? alpha('#10b981', isDark ? 0.2 : 0.12)
              : 'transparent',
          transition: 'background-color 0.5s ease',
          '&:hover': { bgcolor: isSelected ? alpha(cat.color, isDark ? 0.2 : 0.12) : theme.palette.action.hover },
        }}
      >
        <AvatarGroup
          max={2}
          spacing="small"
          sx={{ flexShrink: 0, alignItems: 'center', '& .MuiAvatar-root': { width: 34, height: 34 } }}
        >
          {parties.slice(0, 2).map((p) => roleAvatar(p))}
        </AvatarGroup>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
            >
              {chatDisplayTitle(chat)}
            </Typography>
            <Typography variant="caption" color={isFlashing ? 'success.main' : 'text.disabled'} sx={{ flexShrink: 0, fontWeight: isFlashing ? 700 : 400 }}>
              {formatRelative(chat.lastMessage?.sentAt || chat.updatedAt)}
            </Typography>
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: isFlashing ? 'success.main' : 'text.secondary',
              fontWeight: isFlashing ? 700 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mt: 0.25,
            }}
          >
            {lastPreview}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              icon={cat.icon as any}
              label={cat.label}
              sx={{
                height: 18,
                bgcolor: alpha(cat.color, isDark ? 0.25 : 0.12),
                color: cat.color,
                '& .MuiChip-icon': { color: cat.color, fontSize: 12, ml: '4px' },
                '& .MuiChip-label': { px: 0.75, fontSize: 10, fontWeight: 600 },
              }}
            />
            {chat.status !== 'active' && (
              <Chip
                size="small"
                label={STATUS_META[chat.status]?.label || chat.status}
                color={STATUS_META[chat.status]?.color || 'default'}
                sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
              />
            )}
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, mr: 'auto' }}>
              {(chat.totalMessages || 0).toLocaleString('fa-IR')} پیام
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderTicks = (message: OversightMessage) => {
    if (!selectedChat || message.isSystemMessage) return null;
    const senderId = message.senderId;
    const readEntries = (message.readBy || []).filter((r) => String(r.userId) !== String(senderId));
    const deliveredEntries = (message.deliveredTo || []).filter((d) => String(d.userId) !== String(senderId));

    const nameOf = (id: string) => {
      const p = selectedChat.participants.find((pp) => pp.userId?._id === String(id));
      return p ? userDisplayName(p.userId) : 'کاربر';
    };

    if (readEntries.length > 0) {
      return (
        <Tooltip
          title={
            <Box sx={{ direction: 'rtl' }}>
              {readEntries.map((r, i) => (
                <div key={i}>خوانده شده توسط {nameOf(String(r.userId))} — {formatDateTime(r.readAt)}</div>
              ))}
            </Box>
          }
        >
          <DoneAll sx={{ fontSize: 14, color: '#0ea5e9' }} />
        </Tooltip>
      );
    }
    if (deliveredEntries.length > 0) {
      return (
        <Tooltip
          title={
            <Box sx={{ direction: 'rtl' }}>
              {deliveredEntries.map((d, i) => (
                <div key={i}>تحویل شده به {nameOf(String(d.userId))} — {formatDateTime(d.deliveredAt)}</div>
              ))}
            </Box>
          }
        >
          <DoneAll sx={{ fontSize: 14, color: 'text.disabled' }} />
        </Tooltip>
      );
    }
    return <Done sx={{ fontSize: 14, color: 'text.disabled' }} />;
  };

  const bubbleColors = (role: string) => {
    const base = ROLE_COLOR[role] || '#64748b';
    return {
      bg: alpha(base, isDark ? 0.16 : 0.07),
      border: alpha(base, isDark ? 0.4 : 0.25),
      text: base,
    };
  };

  const renderMessage = (message: OversightMessage) => {
    if (message.isSystemMessage || message.messageType === 'system' || message.systemData?.action) {
      const isVerdict = message.systemData?.action === 'dispute_verdict';
      const meta = message.systemData?.metadata;
      if (isVerdict) {
        return (
          <Box key={message._id} sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
            <Paper
              elevation={0}
              sx={{ maxWidth: 440, width: '100%', overflow: 'hidden', border: `1px solid ${alpha('#8b5cf6', 0.5)}`, borderRadius: 2.5 }}
            >
              <Box sx={{ bgcolor: '#7c3aed', color: '#fff', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Gavel sx={{ fontSize: 17 }} />
                <Typography variant="subtitle2" fontWeight={700}>نتیجه داوری</Typography>
              </Box>
              <Box sx={{ p: 1.75 }}>
                {meta?.decision && <Chip size="small" color="secondary" label={meta.decision} sx={{ mb: 1 }} />}
                {meta?.summary && <Typography variant="body2" sx={{ mb: 1 }}>{meta.summary}</Typography>}
                {meta?.notes && (
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: 'action.hover', mb: 1, borderRadius: 1.5 }}>
                    <Typography variant="caption">{meta.notes}</Typography>
                  </Paper>
                )}
                <Typography variant="caption" color="text.secondary">
                  {meta?.resolvedByName ? `داور: ${meta.resolvedByName} · ` : ''}
                  {formatDateTime(message.sentAt)}
                </Typography>
              </Box>
            </Paper>
          </Box>
        );
      }
      return (
        <Box key={message._id} sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
          <Chip
            size="small"
            variant="outlined"
            label={`${message.content || message.systemData?.action || 'رویداد سیستمی'} · ${formatTime(message.sentAt)}`}
            sx={{ maxWidth: '92%', height: 'auto', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal', fontSize: 11 } }}
          />
        </Box>
      );
    }

    const isAdminMsg = message.senderRole === 'admin';
    const isSideStart = message.senderRole === 'seller' || message.senderRole === 'party_b';
    const colors = bubbleColors(message.senderRole);
    const imgSrc = message.messageType === 'image' || message.file ? imageSrcOf(message.file) : null;
    const RoleIcon = message.senderRole === 'seller' ? Storefront : isAdminMsg ? AdminPanelSettings : Person;

    return (
      <Box
        key={message._id}
        sx={{
          display: 'flex',
          justifyContent: isAdminMsg ? 'center' : isSideStart ? 'flex-start' : 'flex-end',
          my: 0.5,
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: '88%', md: '70%' },
            minWidth: 130,
            px: 1.5,
            py: 1,
            bgcolor: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: 2.5,
            borderStartStartRadius: isSideStart ? 4 : 20,
            borderStartEndRadius: isSideStart ? 20 : 4,
            opacity: message.isDeleted ? 0.55 : 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.4 }}>
            <RoleIcon sx={{ fontSize: 13, color: colors.text }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: colors.text }}>
              {message.senderName}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 10, color: alpha(colors.text, 0.75) }}>
              {ROLE_FA[message.senderRole] || message.senderRole}
            </Typography>
          </Box>

          {message.replyTo?.content && (
            <Box
              sx={{
                px: 1,
                py: 0.5,
                mb: 0.6,
                bgcolor: theme.palette.action.hover,
                borderInlineStart: `3px solid ${alpha(colors.text, 0.5)}`,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                پاسخ به {message.replyTo.senderName || 'پیام'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {message.replyTo.content}
              </Typography>
            </Box>
          )}

          {imgSrc && (
            <Box sx={{ mb: message.content ? 0.75 : 0 }}>
              <a href={imgSrc} target="_blank" rel="noreferrer">
                <img
                  src={imgSrc}
                  alt={message.file?.originalName || 'تصویر'}
                  loading="lazy"
                  style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 10, display: 'block' }}
                />
              </a>
            </Box>
          )}
          {message.messageType === 'image' && !imgSrc && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'text.disabled' }}>
              <BrokenImage sx={{ fontSize: 17 }} />
              <Typography variant="caption">تصویر در دسترس نیست</Typography>
            </Box>
          )}

          {message.isDeleted ? (
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
              این پیام حذف شده است
            </Typography>
          ) : (
            message.content && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.7 }}>
                {message.content}
              </Typography>
            )
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.4 }}>
            {message.isEdited && (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: 9.5 }}>
                ویرایش‌شده
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
              {formatTime(message.sentAt)}
            </Typography>
            {renderTicks(message)}
          </Box>
        </Box>
      </Box>
    );
  };

  /** پیام‌ها + جداکنندهٔ روز */
  const renderMessagesWithDays = () => {
    const out: React.ReactNode[] = [];
    let lastDay = '';
    messages.forEach((m) => {
      const day = dayKeyOf(m.sentAt);
      if (day && day !== lastDay) {
        lastDay = day;
        out.push(
          <Box key={`day-${day}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1.5 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography
              variant="caption"
              sx={{
                px: 1.5,
                py: 0.25,
                borderRadius: 5,
                bgcolor: theme.palette.action.hover,
                color: 'text.secondary',
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {dayLabelOf(m.sentAt)}
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
        );
      }
      out.push(renderMessage(m));
    });
    return out;
  };

  // ------------------------------------------------------------------
  // Conversation pane
  // ------------------------------------------------------------------

  const selectedCategory = selectedChat ? categoryOfChat(selectedChat) : null;

  const infoPanel = selectedChat && (
    <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.03), borderBottom: `1px solid ${theme.palette.divider}` }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem />}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
            شرکت‌کنندگان
          </Typography>
          <Stack spacing={0.75}>
            {selectedChat.participants.map((p) => (
              <Box key={`${p.userId?._id}-${p.role}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {roleAvatar(p, 26)}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {userDisplayName(p.userId)}
                    {!p.isActive && ' (خارج‌شده)'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', direction: 'ltr', textAlign: 'right' }}>
                    {[ROLE_FA[p.role], p.userId?.phone, p.userId?.storeName].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                {p.unreadCount > 0 && (
                  <Tooltip title="پیام‌های خوانده‌نشدهٔ این کاربر">
                    <Chip size="small" color="error" label={p.unreadCount.toLocaleString('fa-IR')} sx={{ height: 17, '& .MuiChip-label': { px: 0.6, fontSize: 10 } }} />
                  </Tooltip>
                )}
              </Box>
            ))}
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
            جزئیات
          </Typography>
          <Stack spacing={0.4}>
            {selectedChat.title && (
              <Typography variant="caption"><b>عنوان:</b> {selectedChat.title}</Typography>
            )}
            {(selectedChat.orderItemId?.productTitle || selectedChat.productId?.title) && (
              <Typography variant="caption"><b>محصول:</b> {selectedChat.orderItemId?.productTitle || selectedChat.productId?.title}</Typography>
            )}
            {selectedChat.orderId?.orderNumber && (
              <Typography variant="caption" sx={{ direction: 'ltr', textAlign: 'right' }}><b>سفارش:</b> #{selectedChat.orderId.orderNumber}</Typography>
            )}
            <Typography variant="caption"><b>ایجاد:</b> {formatDateTime(selectedChat.createdAt)}</Typography>
            <Typography variant="caption"><b>مجموع پیام‌ها:</b> {(selectedChat.totalMessages || 0).toLocaleString('fa-IR')}</Typography>
            {selectedChat.status === 'closed' && (
              <Typography variant="caption" color="warning.main">
                <b>بسته‌شده</b>
                {selectedChat.closedAt ? ` در ${formatDateTime(selectedChat.closedAt)}` : ''}
                {selectedChat.closeReason ? ` — ${selectedChat.closeReason}` : ''}
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );

  const conversationPane = (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%' }}>
      {!selectedChat ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState
            icon={<Forum />}
            title="یک گفتگو را انتخاب کنید"
            description="برای مشاهدهٔ کامل و زندهٔ پیام‌ها، از لیست کنار یک چت انتخاب کنید."
          />
        </Box>
      ) : (
        <>
          {/* conversation header */}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
              flexShrink: 0,
            }}
          >
            {isMobile && (
              <IconButton size="small" onClick={closeConversation}>
                <ArrowForwardIos sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            <AvatarGroup max={2} spacing="small" sx={{ '& .MuiAvatar-root': { width: 32, height: 32 } }}>
              {mainParties(selectedChat).slice(0, 2).map((p) => roleAvatar(p, 32))}
            </AvatarGroup>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {chatDisplayTitle(selectedChat)}
              </Typography>
              <Typography variant="caption" color={typingUser ? 'success.main' : 'text.secondary'} sx={{ display: 'block' }}>
                {typingUser
                  ? `${typingUser} در حال تایپ…`
                  : chatState.isConnected
                    ? 'نمایش زنده'
                    : 'آفلاین'}
              </Typography>
            </Box>
            {selectedCategory && (
              <Chip
                size="small"
                icon={selectedCategory.icon as any}
                label={selectedCategory.label}
                sx={{
                  bgcolor: alpha(selectedCategory.color, isDark ? 0.25 : 0.12),
                  color: selectedCategory.color,
                  '& .MuiChip-icon': { color: selectedCategory.color },
                  display: { xs: 'none', sm: 'flex' },
                }}
              />
            )}
            <Chip
              size="small"
              label={STATUS_META[selectedChat.status]?.label || selectedChat.status}
              color={STATUS_META[selectedChat.status]?.color || 'default'}
            />
            <Tooltip title="جزئیات گفتگو">
              <IconButton size="small" color={showInfo ? 'primary' : 'default'} onClick={() => setShowInfo((v) => !v)}>
                <InfoOutlined sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Collapse in={showInfo}>{infoPanel}</Collapse>

          {messagesError && (
            <Alert severity="error" sx={{ m: 1, flexShrink: 0 }} onClose={() => setMessagesError(null)}>
              {messagesError}
            </Alert>
          )}

          {/* messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: { xs: 1, md: 2 },
              py: 1.5,
              bgcolor: isDark ? alpha('#000', 0.25) : alpha('#64748b', 0.05),
            }}
          >
            {messagesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={26} />
              </Box>
            ) : messages.length === 0 ? (
              <EmptyState dense icon={<Forum />} title="پیامی ثبت نشده" description="این گفتگو هنوز پیامی ندارد." />
            ) : (
              <>
                {renderMessagesWithDays()}
                <div ref={messagesEndRef} />
              </>
            )}
          </Box>

          {/* footer strip — read-only notice / closed banner */}
          <Box
            sx={{
              px: 2,
              py: 0.75,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
            }}
          >
            {selectedChat.status === 'closed' ? (
              <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                این چت بسته شده است{selectedChat.closeReason ? ` — ${selectedChat.closeReason}` : ''}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.disabled">
                حالت نظارت — فقط مشاهده؛ ارسال پیام از این صفحه غیرفعال است.
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  // ------------------------------------------------------------------
  // List pane
  // ------------------------------------------------------------------

  const listPane = (
    <Box
      sx={{
        width: { xs: '100%', md: 360 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderInlineEnd: { md: `1px solid ${theme.palette.divider}` },
        height: '100%',
        minWidth: 0,
      }}
    >
      {/* search + filters */}
      <Box sx={{ p: 1.25, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="جستجو: نام، موبایل، ایمیل، عنوان…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            sx: { borderRadius: 5 },
          }}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <FormControl fullWidth size="small">
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              IconComponent={ExpandMore}
              sx={{ borderRadius: 5, '& .MuiSelect-select': { py: 0.6, fontSize: 12.5 } }}
            >
              {CATEGORY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              IconComponent={ExpandMore}
              sx={{ borderRadius: 5, '& .MuiSelect-select': { py: 0.6, fontSize: 12.5 } }}
            >
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* chat list */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={26} />
          </Box>
        ) : chats.length === 0 ? (
          <EmptyState dense variant="no-results" title="چتی یافت نشد" description="فیلترها یا عبارت جستجو را تغییر دهید." />
        ) : (
          <>
            {chats.map(renderChatListItem)}
            {chats.length < total && (
              <Box sx={{ p: 1.5, textAlign: 'center' }}>
                <Button size="small" variant="outlined" onClick={loadMore} disabled={loadingMore} sx={{ borderRadius: 5 }}>
                  {loadingMore ? <CircularProgress size={16} /> : `نمایش بیشتر (${(total - chats.length).toLocaleString('fa-IR')} چت دیگر)`}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* count footer */}
      <Box sx={{ px: 1.5, py: 0.5, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {chats.length.toLocaleString('fa-IR')} از {total.toLocaleString('fa-IR')} گفتگو
        </Typography>
      </Box>
    </Box>
  );

  // ------------------------------------------------------------------
  // Page
  // ------------------------------------------------------------------

  return (
    <Box
      sx={{
        p: { xs: 1, md: 2.5 },
        display: 'flex',
        flexDirection: 'column',
        height: { md: 'calc(100vh - 88px)' },
        minHeight: 560,
      }}
    >
      {/* header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha('#0ea5e9', isDark ? 0.25 : 0.12),
            color: '#0ea5e9',
          }}
        >
          <Visibility />
        </Box>
        <Box sx={{ mr: 'auto' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            نظارت بر چت‌ها
          </Typography>
          <Typography variant="caption" color="text.secondary">
            مشاهدهٔ زنده و کامل تمام گفتگوهای کاربران، فروشندگان و پشتیبانی
          </Typography>
        </Box>

        <Chip
          size="small"
          icon={
            <Circle
              sx={{
                fontSize: '9px !important',
                ...(liveOk && {
                  animation: 'oversightPulse 1.6s ease-in-out infinite',
                  '@keyframes oversightPulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.35 },
                  },
                }),
              }}
            />
          }
          label={liveOk ? 'نظارت زنده' : 'آفلاین'}
          color={liveOk ? 'success' : 'default'}
          variant={liveOk ? 'filled' : 'outlined'}
        />
        <Tooltip title="به‌روزرسانی">
          <IconButton
            size="small"
            onClick={() => {
              setPage(1);
              fetchChats(1);
              fetchStats();
            }}
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* compact stats strip */}
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          borderRadius: 2.5,
          mb: 1.5,
          overflowX: 'auto',
          flexShrink: 0,
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {statItems.map((s, i) => (
          <React.Fragment key={s.label}>
            {i > 0 && <Divider orientation="vertical" flexItem />}
            <Box
              sx={{
                flex: 1,
                minWidth: 108,
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(s.color, isDark ? 0.25 : 0.12),
                  color: s.color,
                  flexShrink: 0,
                }}
              >
                {s.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                  {s.value === undefined ? '—' : (s.value || 0).toLocaleString('fa-IR')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, whiteSpace: 'nowrap' }}>
                  {s.label}
                </Typography>
              </Box>
            </Box>
          </React.Fragment>
        ))}
      </Paper>

      {/* main two-pane surface */}
      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          display: 'flex',
          borderRadius: 2.5,
          overflow: 'hidden',
          minHeight: 420,
        }}
      >
        {isMobile ? (selectedChat ? conversationPane : listPane) : (
          <>
            {listPane}
            {conversationPane}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ChatOversight;
